import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Badge, Button, ProgressBar } from '@/components/ui'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import {
  getPersonalManagerDayRows,
  getPersonalManagerPartners,
  listPersonalManagers,
} from '@/services/personalManagerService'
import type { PersonalManagerDayRow, PersonalManagerListItem, PersonalManagerPartner } from '@/types'
import {
  getPersonalManagerBlockProgress,
  getPersonalManagerRowTaskFact,
  getPersonalManagerRowTaskPlan,
  getPersonalManagerRowsSummary,
  getPersonalManagerTaskProgress,
  getPersonalManagerTaskStatus,
  getVisiblePersonalManagerTaskBlocks,
  getVisiblePersonalManagerTasks,
  isPersonalManagerTaskDone,
  todayMskISO,
} from '@/lib/personalManagerTasks'
import { formatCurrency, formatPercentage, formatPartnerDisplay } from '@/lib/utils'
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Search,
  Trophy,
  UserCheck,
  X,
} from 'lucide-react'

type ViewMode = 'blocks' | 'studios' | 'rating'
type StudioFilter = 'all' | 'problems' | 'not_checked' | 'partial'
type BlockState = 'done' | 'partial' | 'not_done'

function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

function managerDisplayName(manager: PersonalManagerListItem) {
  return manager.manager_name || manager.tg_username || manager.manager_user_id.slice(0, 8)
}

function ratio(fact: number, plan: number): number | null {
  return plan > 0 ? (fact / plan) * 100 : null
}

function pctTone(value: number | null): 'success' | 'warning' | 'error' | 'neutral' {
  if (value == null) return 'neutral'
  if (value >= 95) return 'success'
  if (value >= 75) return 'warning'
  return 'error'
}

function getRowSummary(row: PersonalManagerDayRow) {
  const plan = getPersonalManagerRowTaskPlan(row.task_date)
  const fact = getPersonalManagerRowTaskFact(row)
  return {
    plan,
    fact,
    pct: plan > 0 ? (fact / plan) * 100 : 0,
  }
}

function getRowStatus(row: PersonalManagerDayRow): { key: StudioFilter | 'done'; label: string; tone: 'success' | 'warning' | 'error' | 'neutral' } {
  const { fact, plan } = getRowSummary(row)
  if (fact === plan) return { key: 'done', label: 'Проверено', tone: 'success' }
  if (fact === 0) return { key: 'not_checked', label: 'Не проверено', tone: 'neutral' }
  if (fact <= Math.floor(plan / 2)) return { key: 'problems', label: 'Проблемы', tone: 'error' }
  return { key: 'partial', label: 'Частично', tone: 'warning' }
}

function getBlockState(row: PersonalManagerDayRow, block: ReturnType<typeof getVisiblePersonalManagerTaskBlocks>[number]): BlockState {
  const { done, total } = getPersonalManagerBlockProgress(row, block)
  if (done === total) return 'done'
  if (done === 0) return 'not_done'
  return 'partial'
}

function toneClasses(state: BlockState): string {
  if (state === 'done') return 'border-emerald-200 bg-emerald-50 text-emerald-600'
  if (state === 'partial') return 'border-amber-200 bg-amber-50 text-amber-600'
  return 'border-rose-200 bg-rose-50 text-rose-600'
}

function progressColor(state: BlockState): string {
  if (state === 'done') return 'bg-emerald-500'
  if (state === 'partial') return 'bg-amber-400'
  return 'bg-rose-500'
}

function issueComment(row: PersonalManagerDayRow): string {
  if (!isPersonalManagerTaskDone(row.partner_report_status)) return 'Нет отчёта'
  if (!isPersonalManagerTaskDone(row.revenue_status)) return 'Расхождение с CRM'
  if (!isPersonalManagerTaskDone(row.app_status)) return 'Приложение заполнено не вовремя'
  if (!isPersonalManagerTaskDone(row.content_status)) return 'Не выложен пост'
  if (!isPersonalManagerTaskDone(row.partner_notified_status)) return 'Нет ответа франчайзи'
  if (!isPersonalManagerTaskDone(row.reconciliation_status)) return 'Нет таблицы сверки'

  const firstPending = getVisiblePersonalManagerTasks(row.task_date).find(task =>
    !isPersonalManagerTaskDone(getPersonalManagerTaskStatus(row, task))
  )
  return firstPending ? `Не закрыто: ${firstPending.label}` : '—'
}

function renderBlockIndicator(state: BlockState) {
  if (state === 'done') return <Check size={14} />
  if (state === 'partial') return <AlertTriangle size={14} />
  return <X size={14} />
}

export function PersonalManagersControlPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)

  const [managers, setManagers] = useState<PersonalManagerListItem[]>([])
  const [managerPartners, setManagerPartners] = useState<Record<string, PersonalManagerPartner[]>>({})
  const [managerRows, setManagerRows] = useState<Record<string, PersonalManagerDayRow[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAdvanced, setIsAdvanced] = useState<boolean | null>(null)
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('blocks')
  const [expandedManager, setExpandedManager] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState(todayMskISO)
  const [studioFilter, setStudioFilter] = useState<StudioFilter>('all')

  const visibleTasks = useMemo(() => getVisiblePersonalManagerTasks(selectedDate), [selectedDate])
  const visibleBlocks = useMemo(() => getVisiblePersonalManagerTaskBlocks(selectedDate), [selectedDate])

  const reload = () => {
    setLoading(true)
    listPersonalManagers()
      .then(async data => {
        setManagers(data)
        const partnersEntries = await Promise.all(
          data.map(async manager => [manager.manager_user_id, await getPersonalManagerPartners(manager.manager_user_id)] as const)
        )
        const rowsEntries = await Promise.all(
          data.map(async manager => [manager.manager_user_id, await getPersonalManagerDayRows(manager.manager_user_id, selectedDate)] as const)
        )
        setManagerPartners(Object.fromEntries(partnersEntries))
        setManagerRows(Object.fromEntries(rowsEntries))
        setError(null)
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    reload()
  }, [selectedDate])

  useEffect(() => {
    let cancelled = false
    if (!user?.id) return
    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })
    return () => { cancelled = true }
  }, [user?.id, getAccessLevel])

  const managerEntries = useMemo(() => {
    const query = search.trim().toLowerCase()

    return managers
      .map(manager => {
        const allRows = managerRows[manager.manager_user_id] ?? []
        const partners = managerPartners[manager.manager_user_id] ?? []

        let filteredRows = allRows.filter(row => {
          if (query) {
            const textMatch =
              managerDisplayName(manager).toLowerCase().includes(query) ||
              (manager.tg_username ?? '').toLowerCase().includes(query) ||
              formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text).toLowerCase().includes(query) ||
              (row.studio_number ?? '').toLowerCase().includes(query) ||
              (row.city_name ?? '').toLowerCase().includes(query)

            if (!textMatch) return false
          }

          const status = getRowStatus(row)
          if (studioFilter === 'all') return true
          return status.key === studioFilter
        })

        if (filteredRows.length === 0) return null

        const summary = getPersonalManagerRowsSummary(filteredRows, selectedDate)
        const totalPlan = summary.plan
        const totalFact = summary.fact
        const fullyChecked = filteredRows.filter(row => getPersonalManagerRowTaskFact(row) >= getPersonalManagerRowTaskPlan(row.task_date)).length
        const problemStudios = filteredRows.filter(row => getRowStatus(row).key === 'problems').length
        const partialStudios = filteredRows.filter(row => getRowStatus(row).key === 'partial').length
        const uncheckedStudios = filteredRows.filter(row => getRowStatus(row).key === 'not_checked').length

        return {
          manager,
          partners,
          rows: filteredRows,
          totalPlan,
          totalFact,
          pct: totalPlan > 0 ? (totalFact / totalPlan) * 100 : 0,
          fullyChecked,
          problemStudios,
          partialStudios,
          uncheckedStudios,
        }
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
  }, [managers, managerRows, managerPartners, search, studioFilter])

  const rankingEntries = useMemo(() => {
    const query = search.trim().toLowerCase()

    return managers
      .map(manager => {
        const rows = managerRows[manager.manager_user_id] ?? []
        const partners = managerPartners[manager.manager_user_id] ?? []

        if (rows.length === 0) return null

        if (query) {
          const managerMatch =
            managerDisplayName(manager).toLowerCase().includes(query) ||
            (manager.tg_username ?? '').toLowerCase().includes(query)

          const rowsMatch = rows.some(row =>
            formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text).toLowerCase().includes(query) ||
            (row.studio_number ?? '').toLowerCase().includes(query) ||
            (row.city_name ?? '').toLowerCase().includes(query)
          )

          if (!managerMatch && !rowsMatch) return null
        }

        const visibleTasks = getVisiblePersonalManagerTasks(selectedDate)
        const totals = visibleTasks.reduce(
          (acc, task) => {
            const progress = getPersonalManagerTaskProgress(rows, task)
            acc.plan += progress.total
            acc.fact += progress.done
            return acc
          },
          { plan: 0, fact: 0 }
        )

        return {
          manager,
          partnersCount: partners.length,
          studiosCount: rows.length,
          plan: totals.plan,
          fact: totals.fact,
          pct: totals.plan > 0 ? (totals.fact / totals.plan) * 100 : 0,
        }
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
      .sort((a, b) => {
        if (b.pct !== a.pct) return b.pct - a.pct
        if (b.fact !== a.fact) return b.fact - a.fact
        if (a.plan !== b.plan) return a.plan - b.plan
        return managerDisplayName(a.manager).localeCompare(managerDisplayName(b.manager), 'ru', { sensitivity: 'base' })
      })
  }, [managers, managerPartners, managerRows, search, selectedDate])

  const stats = useMemo(() => {
    const totalManagers = managerEntries.length
    const totalStudios = managerEntries.reduce((sum, entry) => sum + entry.rows.length, 0)
    const checkedStudios = managerEntries.reduce((sum, entry) => sum + entry.fullyChecked, 0)
    const problematicStudios = managerEntries.reduce((sum, entry) => sum + entry.problemStudios, 0)
    const totalPlan = managerEntries.reduce((sum, entry) => sum + entry.totalPlan, 0)
    const totalFact = managerEntries.reduce((sum, entry) => sum + entry.totalFact, 0)
    const allRows = Object.values(managerRows).flat()
    const appFilledPlan = allRows.length
    const appFilledFact = allRows.reduce(
      (sum, row) => sum + (isPersonalManagerTaskDone(row.app_status) ? 1 : 0),
      0
    )
    const royaltyFact = managers.reduce((sum, manager) => sum + manager.royalty_paid_fact_rub, 0)
    const royaltyPlan = managers.reduce((sum, manager) => sum + manager.royalty_paid_plan_rub, 0)

    return {
      totalManagers,
      totalStudios,
      checkedStudios,
      problematicStudios,
      pct: totalPlan > 0 ? (totalFact / totalPlan) * 100 : null,
      problemPct: totalStudios > 0 ? (problematicStudios / totalStudios) * 100 : null,
      overallAppPct: ratio(appFilledFact, appFilledPlan),
      overallAppFact: appFilledFact,
      overallAppPlan: appFilledPlan,
      overallRoyaltyPct: ratio(royaltyFact, royaltyPlan),
      overallRoyaltyFact: royaltyFact,
      overallRoyaltyPlan: royaltyPlan,
    }
  }, [managerEntries, managerRows, managers])

  if (isAdvanced === false) {
    return <Card><div className="text-sm text-muted-foreground">Эта страница доступна только advanced.</div></Card>
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-start">
        <Button variant="outline" size="sm" onClick={() => navigate('/management/personal-managers')}>
          <ArrowLeft size={16} className="mr-1" />
          Назад
        </Button>
      </div>

      <Card className="p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex rounded-2xl border border-border bg-muted/30 p-1">
            <button
              onClick={() => setViewMode('blocks')}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition ${viewMode === 'blocks' ? 'bg-background text-amber-600 shadow-sm ring-1 ring-amber-200' : 'text-muted-foreground'}`}
            >
              По блокам
            </button>
            <button
              onClick={() => setViewMode('studios')}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition ${viewMode === 'studios' ? 'bg-background text-amber-600 shadow-sm ring-1 ring-amber-200' : 'text-muted-foreground'}`}
            >
              По студиям
            </button>
            <button
              onClick={() => setViewMode('rating')}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition ${viewMode === 'rating' ? 'bg-background text-amber-600 shadow-sm ring-1 ring-amber-200' : 'text-muted-foreground'}`}
            >
              Рейтинг
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 rounded-2xl border border-border bg-background px-3 py-2 lg:min-w-[300px]">
            <button onClick={() => setSelectedDate(prev => addDays(prev, -1))} className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-2 text-sm font-medium">
              <Calendar size={16} className="text-muted-foreground" />
              {new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${selectedDate}T00:00:00`))}
            </div>
            <button onClick={() => setSelectedDate(prev => addDays(prev, 1))} className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(220px,1fr)_auto]">
          {viewMode !== 'rating' && (
            <label className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Поиск по студии или менеджеру"
                className="w-full rounded-2xl border border-border bg-background py-2.5 pl-9 pr-3 text-sm"
              />
            </label>
          )}

          {viewMode !== 'rating' && (
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'Все студии' },
                { key: 'problems', label: 'С проблемами' },
                { key: 'not_checked', label: 'Не проверены' },
                { key: 'partial', label: 'Частично проверены' },
              ].map(item => (
                <button
                  key={item.key}
                  onClick={() => setStudioFilter(item.key as StudioFilter)}
                  className={`rounded-xl border px-3 py-2 text-sm transition ${
                    studioFilter === item.key
                      ? 'border-amber-300 bg-amber-50 text-amber-700'
                      : 'border-border bg-background text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {viewMode !== 'rating' && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
          <Card className="rounded-3xl border border-border/80 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-amber-50 p-3 text-amber-500">
                <UserCheck size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">ЛМ</div>
                <div className="mt-1 text-3xl font-semibold">{stats.totalManagers}</div>
              </div>
            </div>
          </Card>
          <Card className="rounded-3xl border border-border/80 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-amber-50 p-3 text-amber-500">
                <Building2 size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Студий</div>
                <div className="mt-1 text-3xl font-semibold">{stats.totalStudios}</div>
              </div>
            </div>
          </Card>
          <Card className="rounded-3xl border border-border/80 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-500">
                <CheckCircle2 size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Полностью проверено</div>
                <div className="mt-1 flex items-end gap-3">
                  <div className="text-3xl font-semibold">{stats.checkedStudios}</div>
                  <div className="pb-1 text-sm font-medium text-emerald-600">{stats.pct == null ? '—' : formatPercentage(stats.pct)}</div>
                </div>
              </div>
            </div>
          </Card>
          <Card className="rounded-3xl border border-border/80 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-rose-50 p-3 text-rose-500">
                <AlertTriangle size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Проблемные студии</div>
                <div className="mt-1 flex items-end gap-3">
                  <div className="text-3xl font-semibold">{stats.problematicStudios}</div>
                  <div className="pb-1 text-sm font-medium text-rose-500">{stats.problemPct == null ? '—' : formatPercentage(stats.problemPct)}</div>
                </div>
              </div>
            </div>
          </Card>
          <Card className="rounded-3xl border border-border/80 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-sky-50 p-3 text-sky-500">
                <CheckCircle2 size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Приложение</div>
                <div className="mt-1 flex items-end gap-3">
                  <div className="text-3xl font-semibold">
                    {stats.overallAppPct == null ? '—' : formatPercentage(stats.overallAppPct)}
                  </div>
                  <Badge
                    text={stats.overallAppPlan > 0 ? `${stats.overallAppFact} / ${stats.overallAppPlan}` : 'нет плана'}
                    variant={pctTone(stats.overallAppPct)}
                    className="text-xs"
                  />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">Общее заполнение по закреплённым студиям</div>
              </div>
            </div>
          </Card>
          <Card className="rounded-3xl border border-border/80 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-violet-50 p-3 text-violet-500">
                <Circle size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Роялти</div>
                <div className="mt-1 flex items-end gap-3">
                  <div className="text-3xl font-semibold">
                    {stats.overallRoyaltyPct == null ? '—' : formatPercentage(stats.overallRoyaltyPct)}
                  </div>
                  <Badge
                    text={stats.overallRoyaltyPlan > 0 ? `${formatCurrency(stats.overallRoyaltyFact)} / ${formatCurrency(stats.overallRoyaltyPlan)}` : 'нет плана'}
                    variant={pctTone(stats.overallRoyaltyPct)}
                    className="text-xs"
                  />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">Общий сбор роялти по всем ЛМ</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {loading && <Card><div className="text-sm text-muted-foreground">Загрузка…</div></Card>}
      {error && <Card><div className="text-sm text-destructive">{error}</div></Card>}
      {!loading && viewMode === 'rating' && rankingEntries.length === 0 && <Card><div className="text-sm text-muted-foreground">Нет данных по выбранным фильтрам.</div></Card>}
      {!loading && viewMode !== 'rating' && managerEntries.length === 0 && <Card><div className="text-sm text-muted-foreground">Нет данных по выбранным фильтрам.</div></Card>}

      {viewMode === 'rating' ? (
        <Card className="mx-auto w-full max-w-6xl overflow-hidden rounded-3xl border border-border/80 shadow-sm">
          <div className="border-b border-border bg-muted/30 px-5 py-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Trophy size={18} className="text-amber-500" />
              Рейтинг ЛМ по задачам
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              План = все задачи на выбранную дату, факт = только задачи, засчитанные в срок.
            </div>
          </div>

          <div className="divide-y divide-border">
            {rankingEntries.map((entry, index) => (
              <div key={entry.manager.manager_user_id} className="flex flex-col gap-2.5 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-start gap-2.5">
                  <div className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    index === 0
                      ? 'bg-amber-100 text-amber-700'
                      : index === 1
                        ? 'bg-slate-200 text-slate-700'
                        : index === 2
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-muted text-muted-foreground'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[15px] font-semibold">{managerDisplayName(entry.manager)}</div>
                    <div className="mt-0.5 text-sm text-muted-foreground">
                      {entry.partnersCount} франчайзи · {entry.studiosCount} студий
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2.5 lg:min-w-[390px] xl:min-w-[450px]">
                  <div className="flex min-h-[72px] flex-col justify-between rounded-2xl border border-border bg-background px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">План</div>
                    <div className="mt-1 text-base font-semibold">{entry.plan}</div>
                  </div>
                  <div className="flex min-h-[72px] flex-col justify-between rounded-2xl border border-border bg-background px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Факт</div>
                    <div className="mt-1 text-base font-semibold">{entry.fact}</div>
                  </div>
                  <div className="flex min-h-[72px] flex-col justify-between rounded-2xl border border-border bg-background px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">% выполнения</div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="text-base font-semibold">{formatPercentage(entry.pct)}</div>
                      <Badge text={`${entry.fact} / ${entry.plan}`} variant={pctTone(entry.pct)} className="text-xs" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {managerEntries.map(entry => {
              const isOpen = expandedManager === entry.manager.manager_user_id
              return (
                <Card key={entry.manager.manager_user_id} className="overflow-hidden rounded-3xl border border-border/80 shadow-sm">
                  <button
                    className="w-full px-5 py-4 text-left"
                    onClick={() => setExpandedManager(prev => prev === entry.manager.manager_user_id ? null : entry.manager.manager_user_id)}
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {isOpen ? <ChevronDown size={18} className="text-muted-foreground" /> : <ChevronRight size={18} className="text-muted-foreground" />}
                          <div className="truncate text-lg font-semibold">{managerDisplayName(entry.manager)}</div>
                        </div>
                        <div className="mt-1 pl-7 text-sm text-muted-foreground">
                          {entry.partners.length} франчайзи · {entry.rows.length} студий
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 pl-7 lg:pl-0">
                        <div className="text-sm text-muted-foreground">
                          Всего проверено студий: <span className="font-semibold text-foreground">{entry.fullyChecked} / {entry.rows.length}</span>{' '}
                          <span className="font-medium text-amber-500">({Math.round((entry.fullyChecked / Math.max(entry.rows.length, 1)) * 100)}%)</span>
                        </div>
                        <Badge text={formatPercentage(entry.pct)} variant={entry.pct >= 90 ? 'success' : entry.pct >= 60 ? 'warning' : 'error'} className="text-xs" />
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-border bg-muted/20 px-5 py-4">
                      {viewMode === 'blocks' ? (
                        <div className="space-y-3">
                          {visibleBlocks.map(block => {
                            const studiosDone = entry.rows.filter(row => getBlockState(row, block) === 'done').length
                            const studiosPartial = entry.rows.filter(row => getBlockState(row, block) === 'partial').length
                            const blockPct = entry.rows.length > 0 ? (studiosDone / entry.rows.length) * 100 : 0
                            const hasProblems = studiosPartial > 0 || studiosDone < entry.rows.length

                            return (
                              <div key={block.key} className="rounded-2xl border border-border bg-background p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <div className="text-lg font-semibold">{block.label}</div>
                                      {hasProblems && <Badge text="есть проблемы" variant="error" className="text-xs" />}
                                    </div>
                                    <div className="text-sm text-muted-foreground">{studiosDone} / {entry.rows.length} студий</div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="w-40">
                                      <ProgressBar value={blockPct} />
                                    </div>
                                    <div className={`text-sm font-semibold ${blockPct >= 85 ? 'text-emerald-600' : blockPct >= 50 ? 'text-amber-500' : 'text-rose-500'}`}>
                                      {Math.round(blockPct)}%
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-3 grid gap-2">
                                  {entry.rows.map(row => {
                                    const blockState = getBlockState(row, block)
                                    const blockProgress = getPersonalManagerBlockProgress(row, block)
                                    return (
                                      <div key={`${block.key}:${row.studio_internal_id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-border px-3 py-2">
                                        <div className="min-w-0">
                                          <div className="truncate text-sm font-medium">
                                            {row.studio_number || row.studio_internal_id} · {row.city_name || 'Студия'}
                                          </div>
                                          <div className="truncate text-xs text-muted-foreground">
                                            {formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="text-sm font-semibold">{blockProgress.done} / {blockProgress.total}</div>
                                          <div className={`inline-flex h-8 w-8 items-center justify-center rounded-full border ${toneClasses(blockState)}`}>
                                            {renderBlockIndicator(blockState)}
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="overflow-x-auto rounded-2xl border border-border bg-background">
                          <div className="min-w-[1120px]">
                            <div
                              className="grid border-b border-border bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground"
                              style={{ gridTemplateColumns: `minmax(270px,1.6fr) 110px repeat(${visibleBlocks.length}, minmax(90px,1fr)) 110px minmax(180px,1fr)` }}
                            >
                              <div className="px-3 py-3">Студия</div>
                              <div className="px-3 py-3 text-center">Прогресс</div>
                              {visibleBlocks.map(block => (
                                <div key={block.key} className="px-3 py-3 text-center">{block.label}</div>
                              ))}
                              <div className="px-3 py-3 text-center">Статус</div>
                              <div className="px-3 py-3">Комментарий</div>
                            </div>

                            <div className="divide-y divide-border">
                              {entry.rows.map(row => {
                                const summary = getRowSummary(row)
                                const rowStatus = getRowStatus(row)
                                return (
                                  <div
                                    key={row.studio_internal_id}
                                    className="grid items-center"
                                    style={{ gridTemplateColumns: `minmax(270px,1.6fr) 110px repeat(${visibleBlocks.length}, minmax(90px,1fr)) 110px minmax(180px,1fr)` }}
                                  >
                                    <div className="px-3 py-3">
                                      <div className="flex items-center gap-2">
                                        <span className={`h-2.5 w-2.5 rounded-full ${rowStatus.tone === 'success' ? 'bg-emerald-500' : rowStatus.tone === 'warning' ? 'bg-amber-400' : rowStatus.tone === 'error' ? 'bg-rose-500' : 'bg-slate-300'}`} />
                                        <div className="truncate text-sm font-medium">
                                          {row.studio_number || row.studio_internal_id} · {row.city_name || 'Студия'}
                                        </div>
                                      </div>
                                      <div className="truncate pl-4 text-xs text-muted-foreground">
                                        {formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}
                                      </div>
                                    </div>

                                    <div className="px-3 py-3 text-center">
                                      <div className="text-sm font-semibold">{summary.fact} / {summary.plan}</div>
                                    </div>

                                    {visibleBlocks.map(block => {
                                      const blockState = getBlockState(row, block)
                                      return (
                                        <div key={block.key} className="flex items-center justify-center px-3 py-3">
                                          <div className={`inline-flex h-8 w-8 items-center justify-center rounded-full border ${toneClasses(blockState)}`}>
                                            {renderBlockIndicator(blockState)}
                                          </div>
                                        </div>
                                      )
                                    })}

                                    <div className="px-3 py-3 text-center">
                                      <Badge text={rowStatus.label} variant={rowStatus.tone} className="text-xs" />
                                    </div>

                                    <div className="truncate px-3 py-3 text-sm text-muted-foreground">
                                      {rowStatus.key === 'done' ? '—' : issueComment(row)}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              )
            })}
          </div>

          <div className="text-xs text-muted-foreground">
            В режиме «По студиям» блок считается неполным, если внутри него не закрыта хотя бы одна задача.
          </div>
        </>
      )}
    </div>
  )
}
